'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const divingRegionsDropdown = [
  { label: 'Larnaca', href: '/diving-regions/larnaca/' },
  { label: 'Limassol', href: '/diving-regions/limassol/' },
  { label: 'Paphos', href: '/diving-regions/paphos/' },
  { label: 'Ayia Napa & Protaras', href: '/diving-regions/ayia-napa-protaras/' },
  { label: 'Akamas & Latchi', href: '/diving-regions/akamas/' },
];

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRegionsOpen, setIsRegionsOpen] = useState(false);
  const [isMobileRegionsOpen, setIsMobileRegionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsRegionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', icon: 'HomeIcon' },
    { label: 'Dive Sites', href: '/dive-sites', icon: 'MapPinIcon' },
    { label: 'Dive Centres', href: '/dive-centres-directory', icon: 'BuildingOfficeIcon' },
    { label: 'Marine Life', href: '/marine-life', icon: 'SparklesIcon' },
    { label: 'Diving Info', href: '/cyprus-diving-information', icon: 'InformationCircleIcon' },
    { label: 'External Sources', href: '/useful-resources', icon: 'LinkIcon' },
    { label: 'Blog', href: '/blog', icon: 'NewspaperIcon' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 ${className}`} style={{ background: '#0B1622', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
            <div className="relative w-14 h-14 lg:w-16 lg:h-16 rounded-lg overflow-hidden p-1" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <Image
                src="/assets/images/openart-image_IqytUsMC_1769762135737_raw-1769762272452.jpg"
                alt="Cyprus Diving Logo - Octopus with diving gear"
                fill
                className="object-contain"
                priority
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl lg:text-2xl font-headline font-bold" style={{ color: '#EAF4F8' }}>Cyprus Diving</span>
              <span className="text-xs hidden sm:block" style={{ color: '#6FD3E0' }}>Independent Diving Guide</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Home */}
            <Link
              href="/"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              Home
            </Link>

            {/* Dive Sites */}
            <Link
              href="/dive-sites"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              Dive Sites
            </Link>

            {/* Diving Regions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsRegionsOpen(!isRegionsOpen)}
                className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
                style={{ color: isRegionsOpen ? '#6FD3E0' : '#C8DDE6', background: isRegionsOpen ? 'rgba(111,211,224,0.07)' : 'transparent' }}
                onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
                onMouseLeave={(e) => { if (!isRegionsOpen) { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; } }}
                aria-haspopup="true"
                aria-expanded={isRegionsOpen}
              >
                Diving Regions
                <svg
                  className="w-3.5 h-3.5 transition-transform duration-200"
                  style={{ transform: isRegionsOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: '#6FD3E0' }}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isRegionsOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-52 rounded-xl py-2 z-50"
                  style={{ background: '#0F2234', border: '1px solid rgba(111,211,224,0.18)', boxShadow: '0 8px 32px rgba(0,0,0,0.45)' }}
                >
                  {divingRegionsDropdown.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsRegionsOpen(false)}
                      className="block px-4 py-2.5 text-sm font-medium transition-all duration-150"
                      style={{ color: '#C8DDE6' }}
                      onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.08)'; }}
                      onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Dive Centres */}
            <Link
              href="/dive-centres-directory"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              Dive Centres
            </Link>

            {/* Marine Life */}
            <Link
              href="/marine-life"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              Marine Life
            </Link>

            {/* Diving Info */}
            <Link
              href="/cyprus-diving-information"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              Diving Info
            </Link>

            {/* External Sources */}
            <Link
              href="/useful-resources"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              External Sources
            </Link>

            {/* Blog */}
            <Link
              href="/blog"
              className="px-4 py-2 rounded-lg font-medium text-sm tracking-wide transition-all duration-200"
              style={{ color: '#C8DDE6' }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
            >
              Blog
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md transition-colors"
            style={{ color: '#C8DDE6' }}
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex flex-col space-y-1">
              {navLinks.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-md font-medium transition-all duration-200"
                  style={{ color: '#C8DDE6' }}
                  onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
                  onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#C8DDE6'; el.style.background = 'transparent'; }}
                >
                  <Icon name={item.icon as any} size={20} style={{ color: '#6FD3E0' }} />
                  <span>{item.label}</span>
                </Link>
              ))}

              {/* Mobile Diving Regions expandable */}
              <div>
                <button
                  onClick={() => setIsMobileRegionsOpen(!isMobileRegionsOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-md font-medium transition-all duration-200"
                  style={{ color: '#C8DDE6' }}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="GlobeAltIcon" size={20} style={{ color: '#6FD3E0' }} />
                    <span>Diving Regions</span>
                  </div>
                  <svg
                    className="w-4 h-4 transition-transform duration-200"
                    style={{ transform: isMobileRegionsOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: '#6FD3E0' }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isMobileRegionsOpen && (
                  <div className="pl-11 flex flex-col space-y-0.5 pb-1">
                    {divingRegionsDropdown.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => { setIsMobileMenuOpen(false); setIsMobileRegionsOpen(false); }}
                        className="block px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-150"
                        style={{ color: '#A9C3CF' }}
                        onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#6FD3E0'; el.style.background = 'rgba(111,211,224,0.07)'; }}
                        onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.color = '#A9C3CF'; el.style.background = 'transparent'; }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;